import { redirect } from '@tanstack/react-router';

import {
  getFirstPermittedRoute,
  isRouteAvailableInPlatform,
  isRouteUserPermitted,
} from './navigationConfig';

import { RouterContext } from '@/app/router';
import { Platform, UserInfo } from '@/features/users';

export async function loadAndValidatePermissions(
  context: RouterContext,
  location: { pathname: string }
): Promise<void> {
  const promise = context.store.dispatch(
    context.usersApi.endpoints.getUserInfo.initiate(null)
  );

  let platform: Platform['name'];
  let userPermissions: UserInfo['permissions'];
  try {
    const { selectedPlatform, permissions } = await promise.unwrap();

    if (!selectedPlatform) {
      const error = new Error(
        "You don't have access to any platform. Please contact application administrator."
      );
      error.name = 'PlatformError';
      console.error('No platform selected', error);
      throw error;
    }
    platform = selectedPlatform.name;
    userPermissions = permissions;
  } catch (error) {
    if (error instanceof Error && error.name === 'PlatformError') {
      throw error;
    }

    console.error('Failed to load platform', error);
    const err = new Error('Failed to load platform');
    err.name = 'PlatformError';
    throw err;
  } finally {
    promise.unsubscribe();
  }

  if (!isRouteAvailableInPlatform(platform, location.pathname)) {
    redirect({ to: '/', replace: true, throw: true });
  }

  if (!isRouteUserPermitted(platform, location.pathname, userPermissions)) {
    const firstPermittedRoute = getFirstPermittedRoute(
      platform,
      userPermissions
    );

    if (firstPermittedRoute) {
      redirect({
        to: firstPermittedRoute,
        replace: true,
        throw: true,
      });
    } else {
      const error = new Error(
        'You do not have permission to access any routes. Please contact application administrator.'
      );
      error.name = 'PermissionError';
      console.error('No permitted routes', error);
      throw error;
    }
  }
}
